import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, ShoppingBag, Loader2, X, AlertTriangle } from "lucide-react";
import aapi, { inr, fmtDate, formatApiError } from "./aapi";
import { DateTimeFields } from "./DateTimeFields";
import { PageHeader, StatusBadge, EmptyState, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
  { id: "partial", label: "Partial" },
  { id: "refunded", label: "Refunded" },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ status: "", admin_note: "", progress: "", current_count: "", link: "", quantity: "", charge: "", created_at: "", eta: "" });
  const [busy, setBusy] = useState(false);

  const load = () => aapi.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    const c = { all: orders?.length || 0 };
    (orders || []).forEach((o) => (c[o.status] = (c[o.status] || 0) + 1));
    return c;
  }, [orders]);

  const filtered = useMemo(() => {
    if (!orders) return [];
    const q = search.trim().toLowerCase();
    return orders.filter(
      (o) =>
        (tab === "all" || o.status === tab) &&
        (!q ||
          o.order_id.toLowerCase().includes(q) ||
          o.service_name.toLowerCase().includes(q) ||
          o.link.toLowerCase().includes(q) ||
          (o.user_email || "").toLowerCase().includes(q))
    );
  }, [orders, tab, search]);

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      status: o.status,
      admin_note: o.admin_note || "",
      progress: o.progress ?? "",
      current_count: o.current_count ?? "",
      link: o.link || "",
      quantity: o.quantity ?? "",
      charge: o.charge ?? "",
      created_at: toLocalInput(o.created_at),
      eta: toLocalInput(o.eta),
      cancel_progress: o.cancel_progress ?? "",
      cancel_progress_note: o.cancel_progress_note || "",
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { status: form.status, admin_note: form.admin_note };
      if (form.cancel_progress !== "") payload.cancel_progress = parseInt(form.cancel_progress, 10);
      if (form.cancel_progress !== "" || form.cancel_progress_note.trim()) payload.cancel_progress_note = form.cancel_progress_note.trim();
      if (form.progress !== "") payload.progress = parseInt(form.progress, 10);
      if (form.current_count !== "") payload.current_count = parseInt(form.current_count, 10);
      if (form.link.trim()) payload.link = form.link.trim();
      if (form.quantity !== "") payload.quantity = parseInt(form.quantity, 10);
      if (form.charge !== "") payload.charge = parseFloat(form.charge);
      if (form.created_at) payload.created_at = new Date(form.created_at).toISOString();
      payload.eta = form.eta ? new Date(form.eta).toISOString() : "";
      await aapi.put(`/orders/${editing.order_id}`, payload);
      toast.success(`Order ${editing.order_id} updated — audit logged`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const refundWarning = editing && ["cancelled", "refunded"].includes(form.status) && !["cancelled", "refunded"].includes(editing.status);

  return (
    <div data-testid="admin-orders-page">
      <PageHeader title="Order Management" sub="Every order on the platform — inspect, correct, refund." />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-orders-tab-${t.id.replace("_", "-")}`}
            className={`rounded-full border px-4 py-2 text-xs font-medium transition-all duration-300 ${
              tab === t.id ? "border-gold bg-gold/10 text-goldlight" : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
            }`}>
            {t.label}
            <span className="ml-2 font-mono text-[9px] text-neutral-500">{counts[t.id] || 0}</span>
          </button>
        ))}
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order ID, user, service or link"
          className={`${inputCls} pl-11`} data-testid="admin-orders-search" />
      </div>

      {orders === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ShoppingBag} title="No orders match" sub="Adjust the filters above." testid="admin-orders-empty" />
      ) : (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-orders-table">
          <div className="min-w-[960px]">
            <div className="grid grid-cols-12 gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
              <span className="col-span-1">ID</span>
              <span className="col-span-2">User</span>
              <span className="col-span-3">Service</span>
              <span className="col-span-1">Qty</span>
              <span className="col-span-1">Charge</span>
              <span className="col-span-1">Status</span>
              <span className="col-span-2">Date</span>
              <span className="col-span-1 text-right">Edit</span>
            </div>
            {filtered.map((o) => (
              <div key={o.id} data-testid={`admin-order-row-${o.order_id}`}
                className="grid grid-cols-12 items-center gap-3 border-b border-white/5 bg-panel/80 px-5 py-3.5 transition-colors duration-300 last:border-0 hover:bg-gold/[0.04]">
                <span className="col-span-1 font-mono text-xs text-gold/80">{o.order_id}</span>
                <span className="col-span-2 truncate text-xs text-neutral-400" title={o.user_email}>{o.user_email}</span>
                <span className="col-span-3 truncate text-sm text-neutral-200" title={o.service_name}>{o.service_name}</span>
                <span className="col-span-1 text-sm text-neutral-300">{o.quantity.toLocaleString("en-IN")}</span>
                <span className="col-span-1 text-sm font-medium text-neutral-200">{inr(o.charge)}</span>
                <span className="col-span-1"><StatusBadge status={o.status} />{o.cancel_requested && !["cancelled", "refunded", "completed"].includes(o.status) && (
                  <span className="mt-1 inline-block rounded-full bg-amber-400/15 px-1.5 py-0.5 font-mono text-[8px] tracking-wide text-amber-300" data-testid={`admin-order-cancelreq-${o.order_id}`}>CANCEL REQ</span>
                )}</span>
                <span className="col-span-2 font-mono text-[10px] text-neutral-500">{fmtDate(o.created_at)}</span>
                <span className="col-span-1 text-right">
                  <button onClick={() => openEdit(o)} data-testid={`admin-order-edit-${o.order_id}`}
                    className="rounded-full border border-gold/30 px-3 py-1.5 text-[11px] font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black">
                    Edit
                  </button>
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setEditing(null)}>
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }} transition={{ duration: 0.5, ease: EASE }}
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-gold/20 bg-panel p-8"
              onClick={(e) => e.stopPropagation()} data-testid="admin-order-edit-modal">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">EDIT ORDER</div>
                  <h3 className="mt-2 text-2xl font-bold text-neutral-50">{editing.order_id}</h3>
                  <p className="mt-1 text-xs text-neutral-500">{editing.user_email} · {inr(editing.charge)}</p>
                </div>
                <button onClick={() => setEditing(null)} data-testid="admin-order-modal-close" aria-label="Close"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-neutral-400 hover:border-gold/50 hover:text-neutral-100">
                  <X size={16} />
                </button>
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={`${selectCls} mt-2`} data-testid="admin-order-status-select">
                    {TABS.slice(1).map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {refundWarning && (
                  <p className="flex gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-xs text-amber-300" data-testid="refund-warning">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    Saving will refund {inr(editing.charge)} to the customer's wallet and notify them.
                  </p>
                )}

                {(editing.cancel_requested || ["cancelled", "refunded"].includes(form.status)) && (
                  <div className="rounded-xl border border-amber-400/25 bg-amber-400/[0.06] p-4" data-testid="cancel-progress-editor">
                    <label className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300/90">
                      Cancellation progress
                      <span className="text-goldlight" data-testid="cancel-progress-value">
                        {form.cancel_progress === "" ? "—" : `${form.cancel_progress}%`}
                      </span>
                    </label>
                    <select value={form.cancel_progress}
                      onChange={(e) => setForm({ ...form, cancel_progress: e.target.value })}
                      className={`${selectCls} mt-2`} data-testid="cancel-progress-select">
                      <option value="">— Set nahi kiya —</option>
                      {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((p) => (
                        <option key={p} value={p}>{p}%</option>
                      ))}
                    </select>
                    <input value={form.cancel_progress_note}
                      onChange={(e) => setForm({ ...form, cancel_progress_note: e.target.value })}
                      maxLength={140}
                      placeholder='Short note — e.g. "Supplier se confirm ho gaya, refund process ho raha hai"'
                      className={`${inputCls} mt-2 text-xs`} data-testid="cancel-progress-note-input" />
                    {/* quick visual preview of what the customer will see */}
                    {form.cancel_progress !== "" && (
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                        <div className="h-full rounded-full bg-gradient-to-r from-golddark via-gold to-goldlight transition-all duration-500"
                          style={{ width: `${form.cancel_progress}%` }} />
                      </div>
                    )}
                    <p className="mt-2 font-mono text-[9px] leading-relaxed tracking-[0.08em] text-neutral-500">
                      Customer ke panel mein ye percentage cancellation progress bar ke saath dikhega. Cancelled/Refunded save karne pe auto 100% ho jayega.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Progress %</label>
                    <input type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })}
                      className={`${inputCls} mt-2`} data-testid="admin-order-progress-input" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Current count</label>
                    <input type="number" min="0" value={form.current_count} onChange={(e) => setForm({ ...form, current_count: e.target.value })}
                      className={`${inputCls} mt-2`} data-testid="admin-order-count-input" />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Order link</label>
                  <input type="url" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })}
                    placeholder="https://…" className={`${inputCls} mt-2`} data-testid="admin-order-link-input" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Quantity</label>
                    <input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      className={`${inputCls} mt-2`} data-testid="admin-order-quantity-input" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Charge (₹)</label>
                    <input type="number" min="0" step="0.01" value={form.charge} onChange={(e) => setForm({ ...form, charge: e.target.value })}
                      className={`${inputCls} mt-2`} data-testid="admin-order-charge-input" />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Placed at (date & time)</label>
                  <div className="mt-2">
                    <DateTimeFields value={form.created_at} onChange={(v) => setForm({ ...form, created_at: v })} testid="admin-order-placed-at" />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Estimated delivery (customer ko dikhta hai)</label>
                  <div className="mt-2">
                    <DateTimeFields value={form.eta} onChange={(v) => setForm({ ...form, eta: v })} testid="admin-order-eta" allowEmpty />
                  </div>
                </div>

                <p className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-[10px] leading-relaxed text-neutral-500" data-testid="order-edit-record-note">
                  Link, quantity, charge and placed-at corrections update the record only — wallet balances never move.
                  To refund money, switch status to Cancelled or Refunded.
                </p>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Internal admin note</label>
                  <textarea rows={3} value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })}
                    placeholder="Only admins see this" className={`${inputCls} mt-2 resize-none`} data-testid="admin-order-note-input" />
                </div>

                <button onClick={save} disabled={busy} data-testid="admin-order-save-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-50">
                  {busy && <Loader2 size={15} className="animate-spin" />}
                  SAVE CHANGES
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
